import { useEffect, useMemo, useState } from 'react'

export type Cell = string
export type Rows = Cell[][]

/** A page of rows is one screenful, not one sheet of paper. */
const ROWS_PER_PAGE = 100

function columnName(index: number): string {
  let name = ''
  let remaining = index
  while (remaining >= 0) {
    name = String.fromCharCode(65 + (remaining % 26)) + name
    remaining = Math.floor(remaining / 26) - 1
  }
  return name
}

/**
 * Renders a sheet the way it arrived: no column is dropped and no value is
 * reformatted, so what the extraction read and what the user sees are the same
 * grid. Both axes scroll, since a spreadsheet is routinely wider than a screen.
 */
export function DataTable({
  rows,
  /** Row numbers and column letters, as a spreadsheet shows them. */
  showGridHeaders = true,
  /** Paging is for the full view; a preview shows its first rows and stops. */
  paginate = true,
  maxPreviewRows = 12,
}: {
  rows: Rows
  showGridHeaders?: boolean
  paginate?: boolean
  maxPreviewRows?: number
}) {
  const [page, setPage] = useState(1)

  const pageCount = paginate ? Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE)) : 1

  useEffect(() => {
    setPage(1)
  }, [rows])

  const columnCount = useMemo(
    () => rows.reduce((widest, row) => Math.max(widest, row.length), 0),
    [rows],
  )

  const { visibleRows, firstRowNumber } = useMemo(() => {
    if (!paginate) {
      return { visibleRows: rows.slice(0, maxPreviewRows), firstRowNumber: 1 }
    }
    const start = (page - 1) * ROWS_PER_PAGE
    return { visibleRows: rows.slice(start, start + ROWS_PER_PAGE), firstRowNumber: start + 1 }
  }, [rows, page, paginate, maxPreviewRows])

  if (rows.length === 0) {
    return <p className="px-4 py-6 text-xs text-gray-400">This sheet is empty.</p>
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-max border-collapse text-xs">
          {showGridHeaders && (
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="sticky left-0 z-20 border border-gray-200 bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-400" />
                {Array.from({ length: columnCount }, (_, column) => (
                  <th
                    key={column}
                    className="border border-gray-200 bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-500"
                  >
                    {columnName(column)}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {visibleRows.map((row, rowIndex) => (
              <tr key={firstRowNumber + rowIndex} className="even:bg-gray-50/60">
                {showGridHeaders && (
                  <td className="sticky left-0 z-10 border border-gray-200 bg-gray-100 px-2 py-1 text-right text-[10px] tabular-nums text-gray-400">
                    {firstRowNumber + rowIndex}
                  </td>
                )}
                {Array.from({ length: columnCount }, (_, column) => (
                  <td
                    key={column}
                    className="max-w-xs truncate border border-gray-200 px-2 py-1 align-top text-gray-700"
                    title={row[column] || undefined}
                  >
                    {row[column] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {paginate && pageCount > 1 && (
        <div className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-white px-3 py-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-30"
          >
            ← Prev
          </button>
          <span className="text-xs text-gray-400">
            Rows {firstRowNumber}–{firstRowNumber + visibleRows.length - 1} of {rows.length}
          </span>
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
            className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-30"
          >
            Next →
          </button>
        </div>
      )}

      {!paginate && rows.length > maxPreviewRows && (
        <p className="shrink-0 border-t border-gray-200 bg-white px-3 py-1.5 text-[11px] text-gray-400">
          {rows.length - maxPreviewRows} more rows
        </p>
      )}
    </div>
  )
}
